import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { setting } from '../../../../setting';

declare var Square: any;

@Injectable({
  providedIn: 'root'
})
export class SquareService {

private payments: any;
  private card: any;
  private cardButton: any;

  // Estados del servicio
  private isInitialized = false;
  private sdkLoaded = false;

  constructor() {}

  /**
   * Carga el SDK de Square dinámicamente
   */
  private loadSquareSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Si ya está cargado, resolvemos inmediatamente
      if (typeof Square !== 'undefined' && this.sdkLoaded) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = environment.production
        ? 'https://web.squarecdn.com/v1/square.js'
        : 'https://sandbox.web.squarecdn.com/v1/square.js';

      script.type = 'text/javascript';
      script.onload = () => {
        this.sdkLoaded = true;
        resolve();
      };
      script.onerror = (error) => {
        console.error('Error cargando SDK de Square:', error);
        reject(new Error('No se pudo cargar el SDK de Square'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Inicializa Square con las credenciales
   */
  async initializeSquare(): Promise<boolean> {
    try {
      // 1. Cargar SDK
      await this.loadSquareSDK();

      // 2. Verificar credenciales
      if (!setting.squareApplicationId || !setting.squareLocationId) {
        throw new Error('Credenciales de Square no configuradas');
      }

      // 3. Inicializar Payments SDK
      this.payments = Square.payments(
        setting.squareApplicationId,
        setting.squareLocationId
      );

      this.isInitialized = true;
      console.log('Square inicializado correctamente');
      return true;

    } catch (error) {
      console.error('Error inicializando Square:', error);
      this.isInitialized = false;
      return false;
    }
  }

  /**
   * Crea y adjunta el formulario de tarjeta al contenedor especificado
   */
async createCard(containerId: string, maxRetries: number = 3): Promise<boolean> {
  let retries = 0;

  while (retries < maxRetries) {
    try {
      console.log(`🔄 Intento ${retries + 1} de ${maxRetries} para crear formulario`);

      const container = document.getElementById(containerId);

      if (!container) {
        console.warn(`⚠️ Contenedor #${containerId} no encontrado, reintentando...`);
        retries++;

        // Esperar antes de reintentar
        await new Promise(resolve => setTimeout(resolve, 300));
        continue;
      }

      console.log('✅ Contenedor encontrado, creando formulario...');

      // Verificar si el contenedor es visible
      const isVisible = container.offsetParent !== null;
      console.log('📊 Visibilidad del contenedor:', {
        offsetParent: container.offsetParent,
        display: container.style.display,
        visibility: container.style.visibility,
        isVisible: isVisible
      });

      if (!isVisible) {
        // Forzar visibilidad temporalmente
        const originalDisplay = container.style.display;
        container.style.display = 'block';

        try {
          this.card = await this.payments.card();
          await this.card.attach(`#${containerId}`);
          return true;
        } finally {
          // Restaurar display original si es necesario
          container.style.display = originalDisplay;
        }
      } else {
        this.card = await this.payments.card();
        await this.card.attach(`#${containerId}`);
        return true;
      }

    } catch (error) {
      console.error(`❌ Error en intento ${retries + 1}:`, error);
      retries++;

      if (retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  }

  console.error(`❌ Falló después de ${maxRetries} intentos`);
  return false;
}

  /**
   * Crea un botón de pago personalizado
   */
  async createCardButton(containerId: string, label: string = 'Pagar'): Promise<boolean> {
    if (!this.isInitialized) {
      throw new Error('Square no ha sido inicializado');
    }

    try {
      this.cardButton = await this.payments.cardButton({
        style: {
          '.button': {
            backgroundColor: '#28a745',
            color: 'white',
            fontWeight: 'bold',
            padding: '12px 24px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer'
          }
        }
      });

      await this.cardButton.attach(`#${containerId}`);
      return true;

    } catch (error) {
      console.error('Error creando botón de pago:', error);
      return false;
    }
  }

  /**
   * Tokeniza la tarjeta (genera el nonce)
   */
  async tokenizeCard(): Promise<string | null> {
    if (!this.card) {
      throw new Error('No hay formulario de tarjeta inicializado');
    }

    try {
      const result = await this.card.tokenize();

      if (result.status === 'OK') {
        console.log('Tokenización exitosa:', result.token);
        return result.token;
      } else {
        console.error('Error en tokenización:', result.errors);
        this.handleTokenizationErrors(result.errors);
        return null;
      }
    } catch (error) {
      console.error('Error tokenizando tarjeta:', error);
      return null;
    }
  }

  /**
   * Valida los datos de la tarjeta antes de tokenizar
   */
  async validateCard(): Promise<{ valid: boolean; errors: any[] }> {
    if (!this.card) {
      return { valid: false, errors: [{ field: 'card', message: 'Formulario no inicializado' }] };
    }

    try {
      const validationResult = await this.card.validate();
      return validationResult;
    } catch (error) {
      console.error('Error validando tarjeta:', error);
      return { valid: false, errors: [{ field: 'system', message: 'Error de validación' }] };
    }
  }

  /**
   * Maneja errores de tokenización
   */
  private handleTokenizationErrors(errors: any[]): void {
    errors.forEach(error => {
      switch (error.field) {
        case 'cardNumber':
          console.error('Número de tarjeta inválido');
          break;
        case 'expirationDate':
          console.error('Fecha de expiración inválida');
          break;
        case 'cvv':
          console.error('CVV inválido');
          break;
        case 'postalCode':
          console.error('Código postal inválido');
          break;
        default:
          console.error('Error de pago:', error.message);
      }
    });
  }

  /**
   * Limpia y destruye el formulario
   */
  destroyCard(): void {
    if (this.card) {
      // Square no tiene un método destroy explícito, pero podemos limpiar la referencia
      this.card = null;
    }
    if (this.cardButton) {
      this.cardButton = null;
    }
  }

  /**
   * Verifica si Square está listo para usar
   */
  isReady(): boolean {
    return this.isInitialized && this.sdkLoaded;
  }

  /**
   * Método auxiliar para procesar pagos con Square
   */
  async processSquarePayment(amount: number, metadata: any): Promise<{
    success: boolean;
    nonce?: string;
    error?: string
  }> {
    try {
      // 1. Validar tarjeta
      const validation = await this.validateCard();
      if (!validation.valid) {
        return {
          success: false,
          error: 'Datos de tarjeta inválidos: ' +
            validation.errors.map(e => e.message).join(', ')
        };
      }

      // 2. Tokenizar
      const nonce = await this.tokenizeCard();
      if (!nonce) {
        return { success: false, error: 'Error al tokenizar la tarjeta' };
      }

      // 3. Preparar datos para backend
      const paymentData = {
        nonce,
        amount,
        currency: metadata.currency || 'USD',
        locationId: setting.squareLocationId,
        buyer: {
          email: metadata.email,
          name: metadata.name,
          // Otros datos del comprador si los tienes
        },
        metadata: {
          itinerary_id: metadata.itineraryId,
          agent_id: metadata.agentId,
          // ... otros metadatos
        }
      };

      console.log('Datos de pago listos para enviar al backend:', paymentData);

      return { success: true, nonce };

    } catch (error: any) {
      console.error('Error procesando pago con Square:', error);
      return { success: false, error: error.message || 'Error desconocido' };
    }
  }
}
